import { css, unsafeCSS } from 'lit';
import { hostReset, focusRing } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

// ─────────────────────────────────────────────────────────────────────────────
// Shared size constants — must be wrapped with unsafeCSS() before use in css``
// ─────────────────────────────────────────────────────────────────────────────
// Step circle diameter — tracks the shared control-height token so the stepper
// scales with the size presets (≈33px at the default scale, overridable via
// --uwc-step-indicator-size).
const INDICATOR_SIZE      = unsafeCSS(
  'var(--uwc-step-indicator-size, var(--uwc-control-height-md, calc(var(--uwc-font-size-md, 0.875rem) * 2.35)))'
);
const CONNECTOR_THICKNESS = unsafeCSS('2px');

// ── Panel styles ──────────────────────────────────────────────────────────────
export const panelStyles = [
  hostReset,
  focusRing('.uwc-sp__v-indicator', '--uwc-color-primary', '#6366f1'),
  focusRing('.uwc-sp__v-header',    '--uwc-color-primary', '#6366f1'),
  css`
    /* ── Host ─────────────────────────────────────────────────────── */
    :host {
      display: block;
    }

    /* Horizontal: hide inactive panels */
    :host([orientation="horizontal"]:not([active])) {
      display: none;
    }

    /* ── Horizontal content wrapper ───────────────────────────────── */
    .uwc-sp__h-wrap {
      padding: var(--uwc-step-content-padding, ${t.space5} 0 ${t.space3});
    }

    /* ── Vertical: full step row ───────────────────────────────────── */
    .uwc-sp__v-row {
      display:        flex;
      align-items:    stretch;
      gap:            0;
    }

    /* ── Vertical: left column (indicator + connector line) ───────── */
    .uwc-sp__v-left {
      display:        flex;
      flex-direction: column;
      align-items:    center;
      flex-shrink:    0;
      width:          ${INDICATOR_SIZE};
      margin-right:   ${t.space4};
    }

    /* ── Step indicator circle ────────────────────────────────────── */
    .uwc-sp__v-indicator {
      display:         inline-flex;
      align-items:     center;
      justify-content: center;
      width:           ${INDICATOR_SIZE};
      height:          ${INDICATOR_SIZE};
      border-radius:   ${t.radiusFull};
      border:          var(--uwc-step-indicator-border, 2px solid ${t.border});
      background:      var(--uwc-step-indicator-bg,     ${t.surface});
      color:           var(--uwc-step-indicator-color,  ${t.textSecondary});
      font-size:       ${t.fontSizeSm};
      font-weight:     ${t.fontWeightSemibold};
      flex-shrink:     0;
      cursor:          default;
      padding:         0;
      transition:
        background    ${t.durationBase},
        border-color  ${t.durationBase},
        color         ${t.durationBase},
        box-shadow    ${t.durationBase};
    }

    :host([active]) .uwc-sp__v-indicator {
      background:   var(--uwc-step-active-bg,    ${t.primary});
      border-color: var(--uwc-step-active-border, ${t.primary});
      color:        var(--uwc-step-active-color,  ${t.colorWhite});
      box-shadow:   0 0 0 4px color-mix(in oklab, ${t.primary} 18%, transparent);
    }

    :host([completed]) .uwc-sp__v-indicator {
      background:   var(--uwc-step-done-bg,    ${t.primary});
      border-color: var(--uwc-step-done-border, ${t.primary});
      color:        var(--uwc-step-done-color,  ${t.colorWhite});
      box-shadow:   none;
    }

    :host([disabled]) .uwc-sp__v-indicator {
      opacity: 0.38;
      cursor: not-allowed;
    }

    /* Clickable indicator (non-linear mode) */
    .uwc-sp__v-indicator.is-clickable {
      cursor: pointer;
    }
    .uwc-sp__v-indicator.is-clickable:hover:not(:disabled) {
      border-color: var(--uwc-color-primary, ${t.primary});
      color:        var(--uwc-color-primary, ${t.primary});
    }

    @media (prefers-reduced-motion: reduce) {
      .uwc-sp__v-indicator { transition: none; }
    }

    /* ── Connector line (between indicator and next step) ─────────── */
    .uwc-sp__v-connector {
      flex:             1;
      width:            ${CONNECTOR_THICKNESS};
      min-height:       ${t.space6};
      background:       var(--uwc-step-connector-color, ${t.border});
      margin:           ${t.space1} 0;
      border-radius:    ${t.radiusFull};
      transition:       background ${t.durationBase};
    }

    .uwc-sp__v-connector.is-completed {
      background: var(--uwc-step-connector-done, ${t.primary});
    }

    @media (prefers-reduced-motion: reduce) {
      .uwc-sp__v-connector { transition: none; }
    }

    /* ── Vertical: right column (header + animated content) ────────── */
    .uwc-sp__v-right {
      flex:       1;
      min-width:  0;
      padding-bottom: ${t.space4};
    }

    /* Last row: no bottom padding needed (no connector below) */
    :host([is-last]) .uwc-sp__v-right {
      padding-bottom: 0;
    }

    /* ── Step header button ────────────────────────────────────────── */
    .uwc-sp__v-header {
      display:     block;
      width:       100%;
      background:  transparent;
      border:      none;
      padding:     0 0 ${t.space2};
      text-align:  left;
      cursor:      default;
      line-height: 1.3;
    }

    .uwc-sp__v-header.is-clickable { cursor: pointer; }

    .uwc-sp__v-title {
      display:     block;
      font-size:   var(--uwc-step-title-font-size, ${t.fontSizeMd});
      font-weight: var(--uwc-step-title-font-weight, ${t.fontWeightSemibold});
      color:       var(--uwc-step-title-color, ${t.textSecondary});
      transition:  color ${t.durationBase};
      line-height: 1.4;
    }

    :host([active]) .uwc-sp__v-title {
      color: var(--uwc-step-active-title-color, ${t.text});
    }
    :host([completed]) .uwc-sp__v-title {
      color: var(--uwc-step-done-title-color, ${t.textSecondary});
    }

    .uwc-sp__v-subtitle {
      display:    block;
      font-size:  ${t.fontSizeSm};
      color:      ${t.textMuted};
      margin-top: ${t.space1};
    }

    @media (prefers-reduced-motion: reduce) {
      .uwc-sp__v-title { transition: none; }
    }

    /* ── Animated content wrapper (WAAPI-controlled) ──────────────── */
    .uwc-sp__v-content-wrap {
      /* height animated in JS; overflow set inline during animation */
    }

    .uwc-sp__v-content {
      padding-bottom: ${t.space3};
    }

    /* ── Vertical nav buttons ──────────────────────────────────────── */
    .uwc-sp__v-nav {
      display:         flex;
      align-items:     center;
      gap:             ${t.space2};
      padding-top:     ${t.space3};
    }
  `,
];

// ── Stepper (container) styles ────────────────────────────────────────────────
export const stepperStyles = [
  hostReset,
  focusRing('.uwc-step__button', '--uwc-color-primary', '#6366f1'),
  css`
    :host {
      display: block;
    }

    /* ── Horizontal layout ────────────────────────────────────────── */
    .uwc-stepper--horizontal {
      display:        flex;
      flex-direction: column;
    }

    /* Step nav bar */
    .uwc-stepper__nav {
      display:     flex;
      align-items: center;
      padding:     ${t.space2} 0 ${t.space5};
      gap:         0;
      overflow-x:  auto;
      scrollbar-width: none;
    }
    .uwc-stepper__nav::-webkit-scrollbar { display: none; }

    /* Individual step container (button + separator) */
    .uwc-step {
      display:     flex;
      align-items: center;
      flex:        1;
      min-width:   0;
    }

    /* Last step: no flex grow on separator */
    .uwc-step:last-child {
      flex: 0 0 auto;
    }

    /* Step clickable button */
    .uwc-step__button {
      display:          inline-flex;
      align-items:      center;
      gap:              ${t.space2};
      background:       transparent;
      border:           none;
      padding:          ${t.space1} ${t.space2} ${t.space1} 0;
      cursor:           default;
      white-space:      nowrap;
      border-radius:    ${t.radiusMd};
      transition:       background ${t.durationBase};
      min-width:        0;
    }

    .uwc-step__button.is-clickable {
      cursor: pointer;
    }

    .uwc-step__button.is-clickable:hover {
      background: ${t.hoverBg};
    }

    /* ── Horizontal step indicator circle ─────────────────────────── */
    .uwc-step__indicator {
      display:         inline-flex;
      align-items:     center;
      justify-content: center;
      width:           ${INDICATOR_SIZE};
      height:          ${INDICATOR_SIZE};
      border-radius:   ${t.radiusFull};
      border:          var(--uwc-step-indicator-border, 2px solid ${t.border});
      background:      var(--uwc-step-indicator-bg,     ${t.surface});
      color:           var(--uwc-step-indicator-color,  ${t.textSecondary});
      font-size:       ${t.fontSizeSm};
      font-weight:     ${t.fontWeightSemibold};
      flex-shrink:     0;
      transition:
        background    ${t.durationBase},
        border-color  ${t.durationBase},
        color         ${t.durationBase},
        box-shadow    ${t.durationBase};
    }

    .uwc-step__indicator--active {
      background:  var(--uwc-step-active-bg,    ${t.primary});
      border-color:var(--uwc-step-active-border, ${t.primary});
      color:       var(--uwc-step-active-color,  ${t.colorWhite});
      box-shadow:  0 0 0 4px color-mix(in oklab, ${t.primary} 18%, transparent);
    }

    .uwc-step__indicator--completed {
      background:   var(--uwc-step-done-bg,    ${t.primary});
      border-color: var(--uwc-step-done-border, ${t.primary});
      color:        var(--uwc-step-done-color,  ${t.colorWhite});
      box-shadow:   none;
    }

    .uwc-step__indicator--disabled {
      opacity: 0.38;
    }

    @media (prefers-reduced-motion: reduce) {
      .uwc-step__indicator { transition: none; }
    }

    /* ── Step title / subtitle ────────────────────────────────────── */
    .uwc-step__info {
      display:    flex;
      flex-direction: column;
      gap:        1px;
      min-width:  0;
    }

    .uwc-step__title {
      font-size:   var(--uwc-step-title-font-size,   ${t.fontSizeMd});
      font-weight: var(--uwc-step-title-font-weight,  ${t.fontWeightSemibold});
      color:       var(--uwc-step-title-color,        ${t.textSecondary});
      line-height: 1.3;
      transition:  color ${t.durationBase};
      white-space: nowrap;
      overflow:    hidden;
      text-overflow: ellipsis;
    }

    .uwc-step__title--active    { color: var(--uwc-step-active-title-color, ${t.text}); }
    .uwc-step__title--completed { color: var(--uwc-step-done-title-color, ${t.textSecondary}); }

    .uwc-step__subtitle {
      font-size:  ${t.fontSizeSm};
      color:      ${t.textMuted};
      white-space: nowrap;
      overflow:   hidden;
      text-overflow: ellipsis;
    }

    @media (prefers-reduced-motion: reduce) {
      .uwc-step__title { transition: none; }
    }

    /* ── Separator (line between steps) ───────────────────────────── */
    .uwc-step__separator {
      flex:       1;
      min-width:  ${t.space4};
      height:     ${CONNECTOR_THICKNESS};
      background: var(--uwc-step-connector-color, ${t.border});
      margin:     0 ${t.space2};
      border-radius: ${t.radiusFull};
      transition: background ${t.durationBase};
    }

    .uwc-step__separator--completed {
      background: var(--uwc-step-connector-done, ${t.primary});
    }

    @media (prefers-reduced-motion: reduce) {
      .uwc-step__separator { transition: none; }
    }

    /* ── Content area ─────────────────────────────────────────────── */
    .uwc-stepper__content {
      border-top: 1px solid var(--uwc-step-divider, ${t.borderSubtle});
      padding-top: ${t.space4};
    }

    /* ── Footer (nav buttons — horizontal) ────────────────────────── */
    .uwc-stepper__footer {
      display:         flex;
      align-items:     center;
      justify-content: space-between;
      padding-top:     ${t.space5};
      border-top:      1px solid var(--uwc-step-divider, ${t.borderSubtle});
      margin-top:      ${t.space4};
    }

    /* ── Vertical layout ──────────────────────────────────────────── */
    .uwc-stepper--vertical {
      display: block;
    }
  `,
];
